import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatXPUNT } from '../../utils/helpers';
import { FaCheck, FaTimes, FaTicketAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import './PredictionForm.css';

const PredictionForm = ({ competitionId, predictions, onSubmit, onCancel }) => {
  const { user } = useAuth();
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [score, setScore] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPrediction || !selectedOutcome) {
      toast.error('Please select a prediction and outcome');
      return;
    }

    if (selectedOutcome === 'Correct Score' && !score) {
      toast.error('Please enter the correct score');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        prediction_id: selectedPrediction.id,
        selected_outcome: selectedOutcome,
        score: score || undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  const availablePredictions = predictions.filter(
    p => !p.is_expired && p.status !== 'completed'
  );

  if (availablePredictions.length === 0) {
    return (
      <div className="prediction-form">
        <div className="form-header">
          <h3>Make Your Prediction</h3>
          <button onClick={onCancel} className="close-btn">
            <FaTimes />
          </button>
        </div>
        <div className="empty-state-small">
          <p>No predictions available at the moment</p>
        </div>
      </div>
    );
  }

  const selectedPredData = selectedPrediction 
    ? predictions.find(p => p.id === selectedPrediction.id) 
    : null;

  return (
    <div className="prediction-form">
      <div className="form-header">
        <h3>📊 Make Your Prediction</h3>
        <button onClick={onCancel} className="close-btn">
          <FaTimes />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Select Prediction */}
        <div className="form-group">
          <label>Select Match</label>
          <select
            value={selectedPrediction?.id || ''}
            onChange={(e) => {
              const pred = predictions.find(p => p.id === parseInt(e.target.value));
              setSelectedPrediction(pred);
              setSelectedOutcome('');
              setScore('');
            }}
            required
          >
            <option value="">Choose a match...</option>
            {availablePredictions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.home_team} vs {p.away_team}
              </option>
            ))}
          </select>
        </div>

        {selectedPredData && (
          <>
            {/* Match Info */}
            <div className="match-info-box">
              <div className="match-teams">
                <span className="team">{selectedPredData.home_team}</span>
                <span className="vs">vs</span>
                <span className="team">{selectedPredData.away_team}</span>
              </div>
              {selectedPredData.prediction_time && (
                <div className="match-time">
                  ⏰ {new Date(selectedPredData.prediction_time).toLocaleString()}
                </div>
              )}
              {selectedPredData.requires_ticket && (
                <div className="ticket-info">
                  <FaTicketAlt /> Ticket Required: {formatXPUNT(selectedPredData.ticket_price)}
                </div>
              )}
            </div>

            {/* Select Outcome */}
            <div className="form-group">
              <label>Select Outcome</label>
              <div className="outcomes-grid">
                {selectedPredData.outcomes?.map((outcome) => (
                  <button
                    key={outcome.name}
                    type="button"
                    className={`outcome-btn ${selectedOutcome === outcome.name ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedOutcome(outcome.name);
                      if (outcome.name !== 'Correct Score') {
                        setScore('');
                      }
                    }}
                  >
                    {outcome.name}
                    {outcome.points && (
                      <span className="outcome-points">{outcome.points} pts</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Correct Score Input */}
            {selectedOutcome === 'Correct Score' && (
              <div className="form-group">
                <label>Score (e.g., 2-1)</label>
                <input
                  type="text"
                  placeholder="2-1"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  pattern="^\d+-\d+$"
                  title="Format: home-away (e.g., 2-1)"
                  required
                />
                <small className="hint">Format: home-away (e.g., 2-1)</small>
              </div>
            )}

            {/* Submit */}
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || !selectedOutcome}
              >
                {loading ? 'Submitting...' : <><FaCheck /> Submit Prediction</>}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default PredictionForm;